import { gpHome } from '../../app';
import { IUibModalInstance, ModalService } from '../../../app-common/services/bootstrap-ui-modal';
import {
    AiCreditsPurchaseServiceName,
    AiCreditPack,
    AiCreditPurchasePool,
    IAiCreditsPurchaseApi,
} from '../../services/ai-credits-purchase-contract';
import { ConfirmationDialogService } from '../../../app-common/components/confirmation-dialog';
import { savedCardPromptMarkdown, shouldPromptForSavedCards } from '../ai-credits-purchase/saved-card-prompt';
import {
    defaultPurchasePool,
    purchasablePools,
    purchasePoolLabel,
    shouldOfferPoolChoice,
} from '#gp/app-home/components/ai-credits-purchase/purchase-pool-choice';
import { pickDefaultPack } from './buy-credits-packs';

import type * as ng from 'angular';
import gpBuyCreditsDialogTemplate from './gp-buy-credits-dialog.html';

/** Button results for the saved-card consent dialog (#473). */
const UseSavedCard = 'use-saved-card';
const UseNewCard = 'use-new-card';

interface gpBuyCreditsDialogScope extends ng.IScope {
    packs: AiCreditPack[];
    selectedPack: AiCreditPack | null;
    /**
     * Whether buying is live — for the pool currently SELECTED, since #724. With no
     * pool rows (a server that predates them) it is the endpoint's own billed-pool
     * answer, exactly as before.
     */
    canPurchase: boolean;

    /** The pools the buyer may buy for, in server order (#724). */
    poolChoices: AiCreditPurchasePool[];
    /**
     * Whether to draw the "Add credits to" row: when there is a real choice, and also
     * when the one pool the buyer may top up is not the pool their usage is charged to
     * — money must never land somewhere the dialog hasn't named (#724). A single
     * purchasable pool that is the billed one — an individual with no team, the common
     * case — leaves the dialog as it always looked. Never where the deployment has no
     * packs to sell (#746): there is no purchase to route.
     */
    showPoolChoice: boolean;
    /**
     * The pool this purchase tops up. Null when there is nothing purchasable, and also
     * against a server that sends no pool rows — where a purchase is still possible and
     * goes to the billed pool.
     */
    selectedPool: AiCreditPurchasePool | null;
    /** Option text for a pool, including whether it is the one being billed. */
    poolLabel(pool: AiCreditPurchasePool): string;
    onPoolChange(): void;

    loadingData: boolean;
    loadedData: boolean;
    errorText: string | null;
    comingSoonNote: boolean;
    /** A checkout redirect is being created. */
    starting: boolean;

    selectPack(pack: AiCreditPack): void;
    continueToPayment(): void;
    cancel(): void;
}

gpHome.controller('gpBuyCreditsDialog', [
    '$scope',
    '$uibModalInstance',
    AiCreditsPurchaseServiceName,
    ConfirmationDialogService.serviceName,
    function (
        $scope: gpBuyCreditsDialogScope,
        $uibModalInstance: IUibModalInstance<void>,
        gpAiCreditsPurchaseService: IAiCreditsPurchaseApi,
        gpConfirmationDialogService: ConfirmationDialogService,
    ) {
        $scope.packs = [];
        $scope.selectedPack = null;
        $scope.canPurchase = false;
        $scope.poolChoices = [];
        $scope.showPoolChoice = false;
        $scope.selectedPool = null;
        $scope.loadingData = true;
        $scope.loadedData = false;
        $scope.errorText = null;
        $scope.comingSoonNote = false;
        $scope.starting = false;

        // Whether checkout is configured on this deployment. Kept off the scope: it
        // gates behaviour, and the user is told about it through comingSoonNote.
        let available = false;

        $scope.poolLabel = purchasePoolLabel;

        // Only purchasable pools are ever offered, so this can only ever re-affirm
        // canPurchase — it exists so the purchase buttons follow the selection rather
        // than a value computed once at load.
        $scope.onPoolChange = () => {
            $scope.canPurchase = !!($scope.selectedPool && $scope.selectedPool.canPurchase);
        };

        gpAiCreditsPurchaseService.getOptions()
            .then(options => {
                $scope.packs = options.packs || [];
                available = !!options.available;

                // Which pool is this top-up for? (#724) The buyer's pools arrive
                // whether or not the deployment has anything on sale, so the row is
                // gated on the catalogue as well (#746).
                $scope.poolChoices = purchasablePools(options.pools);
                $scope.showPoolChoice = shouldOfferPoolChoice(options.pools, available);
                $scope.selectedPool = defaultPurchasePool(options.pools);

                $scope.canPurchase = $scope.selectedPool
                    ? !!$scope.selectedPool.canPurchase
                    : !!options.canPurchase;

                $scope.selectedPack = pickDefaultPack($scope.packs);
                $scope.loadedData = true;
            })
            .catch(() => {
                $scope.errorText = 'Unable to load credit packs right now. Please try again.';
            })
            .finally(() => $scope.loadingData = false);

        $scope.selectPack = (pack: AiCreditPack) => {
            if (!$scope.canPurchase) return;
            $scope.selectedPack = pack;
        };

        $scope.continueToPayment = () => {
            const pack = $scope.selectedPack;
            if (!pack || !$scope.canPurchase || $scope.starting) return;

            if (!available) {
                // Checkout isn't configured on this deployment — say so rather than
                // starting a purchase that cannot complete.
                $scope.comingSoonNote = true;
                return;
            }

            $scope.errorText = null;
            $scope.starting = true;

            // Issue #473: a customer with a card on file was being asked to type it
            // again, because Stripe only offers saved cards marked redisplayable and
            // our subscription checkout doesn't mark them. Ask first, then make them
            // available. A failed lookup resolves to null so a Stripe problem sends the
            // buyer to Checkout unchanged rather than blocking the sale.
            gpAiCreditsPurchaseService.getSavedCards()
                .catch(() => null)
                .then(saved => {
                    if (!shouldPromptForSavedCards(saved)) {
                        return goToCheckout(pack, false);
                    }

                    return gpConfirmationDialogService.show({
                        title: 'Use a saved card?',
                        mainInstructionMarkdown: savedCardPromptMarkdown(saved.cards),
                        buttons: [
                            { caption: 'Use saved card', result: UseSavedCard },
                            { caption: 'Enter a new card', result: UseNewCard },
                        ],
                    }).then(
                        (result: string) => goToCheckout(pack, result === UseSavedCard),
                        // Dismissed rather than answered (escape, backdrop): the buyer
                        // decided nothing, so don't send them to Stripe.
                        () => { $scope.starting = false; },
                    );
                });
        };

        function goToCheckout(pack: AiCreditPack, consentToSavedCards: boolean) {
            // The chosen pool rides along (#724); null — no pool rows from the
            // server — sends no owner, which the server reads as the pool being
            // billed, i.e. what this call has always done.
            return gpAiCreditsPurchaseService
                .startCheckout(pack.id, consentToSavedCards, $scope.selectedPool)
                .then(checkoutUrl => {
                    // Leave `starting` set: the page is navigating away.
                    window.location.assign(checkoutUrl);
                })
                .catch(() => {
                    $scope.errorText = 'Unable to start the purchase right now. Please try again.';
                    $scope.starting = false;
                });
        }

        $scope.cancel = () => {
            $uibModalInstance.dismiss('cancel');
        };
    }]);

export class BuyCreditsDialogService {
    static readonly serviceName = 'gpBuyCreditsDialogService';
    static $inject = ['gpModal'];

    constructor(private readonly modalService: ModalService) {
    }

    /**
     * Resolves when the dialog closes, rejects when it is dismissed. A completed
     * purchase never resolves here — it redirects to Stripe and comes back to the
     * account page, where gp-ai-credits-purchase verifies the session.
     */
    showDialog(): ng.IPromise<void> {
        const modalInstance = this.modalService.open<void>({
            animation: true,
            template: gpBuyCreditsDialogTemplate,
            controller: 'gpBuyCreditsDialog',
            backdrop: true,
            backdropClass: 'gameplan-gp-buy-credits-dialog',
            windowClass: 'gameplan-gp-buy-credits-dialog',
        });

        return modalInstance.result;
    }
}

gpHome.service(BuyCreditsDialogService.serviceName, BuyCreditsDialogService);
